import { useState } from 'react';
import { Layers, Loader2 } from 'lucide-react';
import {
  formatStageDuration,
  getModuleLabel,
  getStageLabel,
  moduleStages,
  stagePhotoLabel,
  stageRequiresPhoto,
  type StageForecast,
} from '@mustauto/types';
import { formatApiError, formatDuration } from '@mustauto/utils';
import { SOFT_BADGE_CLASSES, useToast } from '@mustauto/ui/web';
import { useStageConfig } from '@/hooks/useSettings';

/**
 * A etapa da OS dentro do módulo (funilaria: desmontagem → … → montagem) — o
 * mesmo seletor do app, no painel. É o que faz o "Em execução" de doze dias
 * dizer em que ponto o carro está, para o gestor e para o cliente (a timeline
 * do app B2C e a página compartilhada mostram a etapa).
 *
 * Não renderiza nada para OS sem etapas: mecânica, ou OS sem tipo — o status já
 * diz tudo ali. Qualquer etapa pode ser escolhida, inclusive voltar (o carro
 * volta da preparação para a funilaria quando aparece mais um amassado). Não há
 * "limpar etapa", como no app: a etapa só anda, e desmarcar não descreve nada
 * que acontece com o carro.
 *
 * Com a OS encerrada, mostra a última etapa sem deixar trocar — a API também
 * recusaria (409).
 *
 * **Cabine de pintura (F3.4).** Entrar nela exige a foto "Antes da pintura", e
 * o painel não tem câmera: a opção fica travada, com o motivo, até a foto
 * existir na OS (tirada pelo app). Com a foto — de uma entrada anterior, por
 * exemplo —, o painel deixa levar. `hasStagePhoto` ausente é "não sei" (a lista
 * de mídia não carregou): a opção fica livre e a API recusa explicando.
 *
 * **Etapas da oficina (F3.8).** Nome, ordem e quais pedem foto são os da
 * oficina. A etapa escondida não é oferecida; a OS que já está nela aparece
 * com o nome, sem número, e fica até alguém escolher outra.
 */
export function OSStageSelect({
  module,
  stage,
  closed,
  onChange,
  hasStagePhoto,
  forecast,
}: {
  module: string | null | undefined;
  stage: string | null | undefined;
  closed: boolean;
  onChange: (stage: string) => Promise<unknown>;
  hasStagePhoto?: (stage: string) => boolean;
  /** Previsão da etapa atual (F3.7), da API. `null` = sem duração configurada. */
  forecast?: StageForecast | null;
}) {
  const { toast } = useToast();
  // A escolha fica na tela enquanto o PATCH e a releitura andam. Presa só à
  // prop, o seletor voltava para a etapa anterior até a resposta chegar — em
  // rede lenta parecia recusa, e o segundo toque batia no 409 de concorrência.
  const [pending, setPending] = useState<string | null>(null);
  const config = useStageConfig();

  const stages = moduleStages(module, config);
  if (!module || stages.length === 0) return null;

  const shown = pending ?? stage ?? null;
  const index = shown ? stages.indexOf(shown) : -1;
  const position = index >= 0 ? `${index + 1} de ${stages.length}` : null;
  // Etapa gravada fora da lista visível — escondida pelo gestor (F3.8), ou de
  // um app mais novo com outra lista: aparece como está, em vez de "Nenhuma
  // etapa marcada" — que levaria o gestor a sobrescrever algo achando que não
  // havia nada.
  const unknown = shown !== null && index < 0;
  // Etapas travadas por falta de foto — a atual nunca trava.
  const lacksPhoto = (key: string) =>
    key !== shown && stageRequiresPhoto(module, key, config) && hasStagePhoto !== undefined && !hasStagePhoto(key);
  const blocked = stages.filter(lacksPhoto);

  const change = async (next: string) => {
    if (!next || next === shown) return;
    setPending(next);
    try {
      await onChange(next);
    } catch (err) {
      toast({
        type: 'error',
        title: 'Não foi possível mudar a etapa',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
      <Layers size={14} className="text-muted-foreground" aria-hidden />
      <label htmlFor="os-stage" className="text-muted-foreground">
        Etapa de {getModuleLabel(module)}
      </label>
      {closed ? (
        <span className="font-medium">{stage ? getStageLabel(stage, config) : 'Nenhuma etapa marcada'}</span>
      ) : (
        <select
          id="os-stage"
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          value={shown ?? ''}
          onChange={(e) => void change(e.target.value)}
          disabled={pending !== null}
        >
          {shown === null && (
            <option value="" disabled>
              Nenhuma etapa marcada
            </option>
          )}
          {unknown && (
            <option value={shown} disabled>
              {getStageLabel(shown, config)}
            </option>
          )}
          {stages.map((key, i) => (
            <option key={key} value={key} disabled={lacksPhoto(key)}>
              {i + 1}. {getStageLabel(key, config)}
              {lacksPhoto(key) ? ' — falta a foto' : ''}
            </option>
          ))}
        </select>
      )}
      {position && <span className="text-xs text-muted-foreground">{position}</span>}
      {pending !== null && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
      {!closed && pending === null && forecast && forecast.stage === stage && <ForecastText forecast={forecast} />}
      {!closed && blocked.length > 0 && (
        <p className="basis-full text-xs text-muted-foreground">
          {blocked
            .map(
              (key) =>
                `${getStageLabel(key, config)}: exige a foto “${stagePhotoLabel(key, config)}”, tirada pelo app ao levar o carro.`,
            )
            .join(' ')}
        </p>
      )}
    </div>
  );
}

/**
 * "Prevista até qui, 16:00" ou "Atrasada 5 h" (F3.7). Hora aqui, e não só o
 * dia como para o cliente: é o gestor, e ele cobra por turno.
 */
function ForecastText({ forecast }: { forecast: StageForecast }) {
  const expected = new Date(forecast.expectedBy);
  if (forecast.overdue) {
    return (
      <span className={`rounded-md px-1.5 py-0.5 text-xs font-medium ${SOFT_BADGE_CLASSES.amber}`}>
        Atrasada {formatDuration(Date.now() - expected.getTime())}
      </span>
    );
  }
  const when = expected.toLocaleString('pt-BR', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  return (
    <span className="text-xs text-muted-foreground" title={`Tempo da etapa: ${formatStageDuration(forecast.hours)}`}>
      Prevista até {when}
    </span>
  );
}
